import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { AppLayout } from "@/components/layout/app-layout";
import {
  ProtectedRoute,
  PublicRoute,
  SuperadminRoute,
  PermissionRoute,
} from "@/components/layout/protected-route";
import { LoginPage } from "@/pages/auth/login";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password";
import { ResetPasswordPage } from "@/pages/auth/reset-password";
import { DashboardPage } from "@/pages/dashboard";
import { useAuthStore } from "@/stores/auth.store";
import { PERMISSIONS } from "@/constants/permissions";

// Lazy load other pages
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

const CRMPage = lazy(() =>
  import("@/pages/crm").then((m) => ({ default: m.CRMPage })),
);
const JobsPage = lazy(() =>
  import("@/pages/jobs").then((m) => ({ default: m.JobsPage })),
);
const FinancePage = lazy(() =>
  import("@/pages/finance").then((m) => ({ default: m.FinancePage })),
);
const VendorsPage = lazy(() =>
  import("@/pages/vendors").then((m) => ({ default: m.VendorsPage })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings").then((m) => ({ default: m.SettingsPage })),
);
const UsersPage = lazy(() =>
  import("@/pages/users").then((m) => ({ default: m.UsersPage })),
);
const SalesPage = lazy(() =>
  import("@/pages/sales").then((m) => ({ default: m.default })),
);
const AttendancePage = lazy(() =>
  import("@/pages/attendance").then((m) => ({ default: m.default })),
);
const InvoicesPage = lazy(() =>
  import("@/pages/invoices").then((m) => ({ default: m.InvoicesPage })),
);
const InvoiceDetailPage = lazy(() =>
  import("@/pages/invoices/invoice-detail-page").then((m) => ({
    default: m.InvoiceDetailPage,
  })),
);

const ProductsPage = lazy(() =>
  import("@/pages/products").then((m) => ({ default: m.default })),
);

// Superadmin pages
const SuperadminDashboard = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.SuperadminDashboard })),
);
const TenantsPage = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.TenantsPage })),
);
const AdminSettingsPage = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.AdminSettingsPage })),
);
const CommunicationMonitorDashboard = lazy(() =>
  import("@/pages/admin").then((m) => ({
    default: m.CommunicationMonitorDashboard,
  })),
);
const SiteLogsViewer = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.SiteLogsViewer })),
);

// Client Portal pages
const PortalLogin = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalLogin,
  })),
);
const PortalLayout = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalLayout,
  })),
);
const PortalDashboard = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalDashboard,
  })),
);
const PortalMessages = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalMessages,
  })),
);
const PortalNotifications = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalNotifications,
  })),
);
const PortalProfile = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalProfile,
  })),
);
const PortalJobs = lazy(() =>
  import("@/components/client-portal").then((m) => ({ default: m.PortalJobs })),
);
const PortalJobDetail = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalJobDetail,
  })),
);
const PortalInvoices = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalInvoices,
  })),
);
const PortalInvoiceDetail = lazy(() =>
  import("@/components/client-portal").then((m) => ({
    default: m.PortalInvoiceDetail,
  })),
);

// Client Quick Access pages (Magic Link)
const ClientAccessPage = lazy(() => import("@/pages/client-access"));
const ClientPortalLayout = lazy(() => import("@/pages/client-access/layout"));
const ClientDashboard = lazy(() => import("@/pages/client-access/dashboard"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Smart redirect based on role
function RoleBasedRedirect() {
  const user = useAuthStore((state) => state.user);
  if (user?.role === "super_admin") {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RoleBasedRedirect />,
  },
  {
    path: "/login",
    element: (
      <PublicRoute>
        <LoginPage />
      </PublicRoute>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <PublicRoute>
        <ForgotPasswordPage />
      </PublicRoute>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <PublicRoute>
        <ResetPasswordPage />
      </PublicRoute>
    ),
  },
  // Superadmin routes
  {
    path: "/admin",
    element: (
      <SuperadminRoute>
        <AppLayout />
      </SuperadminRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <SuperadminDashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: "tenants",
        element: (
          <SuspenseWrapper>
            <TenantsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <AdminSettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "communications",
        element: (
          <SuspenseWrapper>
            <CommunicationMonitorDashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: "logs",
        element: (
          <SuspenseWrapper>
            <SiteLogsViewer />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Tenant routes
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "dashboard",
        element: <DashboardPage />,
      },
      {
        path: "crm/*",
        element: (
          <PermissionRoute
            permissions={[
              PERMISSIONS.LEADS_VIEW,
              PERMISSIONS.CLIENTS_VIEW,
              PERMISSIONS.CONTACTS_VIEW,
            ]}
          >
            <SuspenseWrapper>
              <CRMPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "jobs/*",
        element: (
          <PermissionRoute permission={PERMISSIONS.JOBS_VIEW}>
            <SuspenseWrapper>
              <JobsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "sales/*",
        element: (
          <PermissionRoute
            permissions={[
              PERMISSIONS.QUOTATIONS_VIEW,
              PERMISSIONS.SALES_ORDERS_VIEW,
            ]}
          >
            <SuspenseWrapper>
              <SalesPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "finance/*",
        element: (
          <PermissionRoute
            permissions={[
              PERMISSIONS.INVOICES_VIEW,
              PERMISSIONS.PAYMENTS_VIEW,
              PERMISSIONS.EXPENSES_VIEW,
            ]}
          >
            <SuspenseWrapper>
              <FinancePage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "invoices",
        element: (
          <PermissionRoute permission={PERMISSIONS.INVOICES_VIEW}>
            <SuspenseWrapper>
              <InvoicesPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "invoices/:id",
        element: (
          <PermissionRoute permission={PERMISSIONS.INVOICES_VIEW}>
            <SuspenseWrapper>
              <InvoiceDetailPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "products",
        element: (
          <PermissionRoute permission={PERMISSIONS.PRODUCTS_VIEW}>
            <SuspenseWrapper>
              <ProductsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "vendors/*",
        element: (
          <PermissionRoute permission={PERMISSIONS.VENDORS_VIEW}>
            <SuspenseWrapper>
              <VendorsPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "settings/*",
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "users",
        element: (
          <PermissionRoute permission={PERMISSIONS.USERS_VIEW}>
            <SuspenseWrapper>
              <UsersPage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "attendance",
        element: (
          <PermissionRoute permission={PERMISSIONS.ATTENDANCE_VIEW}>
            <SuspenseWrapper>
              <AttendancePage />
            </SuspenseWrapper>
          </PermissionRoute>
        ),
      },
      {
        path: "reports",
        element: (
          <PermissionRoute permission={PERMISSIONS.REPORTS_VIEW}>
            <div className="text-center py-12 text-muted-foreground">
              Reports module coming soon...
            </div>
          </PermissionRoute>
        ),
      },
    ],
  },
  {
    path: "/unauthorized",
    element: (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">403</h1>
          <p className="text-muted-foreground mb-4">
            You don't have permission to access this page.
          </p>
          <a href="/dashboard" className="text-primary hover:underline">
            Go to Dashboard
          </a>
        </div>
      </div>
    ),
  },
  // Client Portal Routes
  {
    path: "/portal/login",
    element: (
      <SuspenseWrapper>
        <PortalLogin />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/portal",
    element: (
      <SuspenseWrapper>
        <PortalLayout />
      </SuspenseWrapper>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/portal/dashboard" replace />,
      },
      {
        path: "dashboard",
        element: (
          <SuspenseWrapper>
            <PortalDashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: "jobs",
        element: (
          <SuspenseWrapper>
            <PortalJobs />
          </SuspenseWrapper>
        ),
      },
      {
        path: "jobs/:id",
        element: (
          <SuspenseWrapper>
            <PortalJobDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: "messages",
        element: (
          <SuspenseWrapper>
            <PortalMessages />
          </SuspenseWrapper>
        ),
      },
      {
        path: "notifications",
        element: (
          <SuspenseWrapper>
            <PortalNotifications />
          </SuspenseWrapper>
        ),
      },
      {
        path: "invoices",
        element: (
          <SuspenseWrapper>
            <PortalInvoices />
          </SuspenseWrapper>
        ),
      },
      {
        path: "invoices/:id",
        element: (
          <SuspenseWrapper>
            <PortalInvoiceDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: "profile",
        element: (
          <SuspenseWrapper>
            <PortalProfile />
          </SuspenseWrapper>
        ),
      },
      {
        path: "settings",
        element: (
          <SuspenseWrapper>
            <PortalProfile />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Client Quick Access (Magic Link - No Login Required)
  {
    path: "/client/:token",
    element: (
      <SuspenseWrapper>
        <ClientAccessPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "/client",
    element: (
      <SuspenseWrapper>
        <ClientPortalLayout />
      </SuspenseWrapper>
    ),
    children: [
      {
        path: "dashboard",
        element: (
          <SuspenseWrapper>
            <ClientDashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: "messages",
        element: (
          <SuspenseWrapper>
            <PortalMessages />
          </SuspenseWrapper>
        ),
      },
      {
        path: "jobs",
        element: (
          <SuspenseWrapper>
            <PortalJobs />
          </SuspenseWrapper>
        ),
      },
      {
        path: "jobs/:id",
        element: (
          <SuspenseWrapper>
            <PortalJobDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: "invoices",
        element: (
          <SuspenseWrapper>
            <PortalInvoices />
          </SuspenseWrapper>
        ),
      },
      {
        path: "invoices/:id",
        element: (
          <SuspenseWrapper>
            <PortalInvoiceDetail />
          </SuspenseWrapper>
        ),
      },
      {
        path: "documents",
        element: (
          <SuspenseWrapper>
            <div className="p-6 text-center text-gray-500">
              <p>Documents feature coming soon</p>
            </div>
          </SuspenseWrapper>
        ),
      },
    ],
  },
  {
    path: "*",
    element: (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
          <p className="text-muted-foreground mb-4">Page not found</p>
          <a href="/dashboard" className="text-primary hover:underline">
            Go to Dashboard
          </a>
        </div>
      </div>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
